import { FC, memo, useCallback, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import { AccordionSection } from "../../../components/AccordionSection";
import { LightTooltip } from "@/components/LightTooltip";
import { useCompanyFilterActions, useCompanyFilterSlice } from "../../../hooks";
import {
  getIntentCadenceLabel,
  INTENT_ALL_DATES_LABEL,
  INTENT_CADENCE_OPTIONS,
} from "../constants";

export const SignalTimingSection: FC = memo(() => {
  const slice = useCompanyFilterSlice("intent");
  const { setSlice } = useCompanyFilterActions();
  const [expanded, setExpanded] = useState(false);

  const hasAnySelection =
    slice.signalScore !== "HIGH" || slice.numberOfCadences !== 4;

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setSlice("intent", {
        ...slice,
        signalScore: "HIGH",
        numberOfCadences: 4,
      });
    },
    [setSlice, slice]
  );

  const handleMediumScoreToggle = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSlice("intent", {
        ...slice,
        signalScore: event.target.checked ? "MEDIUM" : "HIGH",
      });
    },
    [setSlice, slice]
  );

  const handleDateRangeChange = useCallback(
    (event: SelectChangeEvent<number | "">) => {
      const val = event.target.value;
      setSlice("intent", {
        ...slice,
        numberOfCadences: val === "" ? null : (val as number),
      });
    },
    [slice, setSlice]
  );

  return (
    <AccordionSection
      title="Signal & Timing"
      expanded={expanded}
      onToggle={() => setExpanded((prev) => !prev)}
      action={
        hasAnySelection ? (
          <Tooltip title="Reset">
            <IconButton
              size="small"
              aria-label="Reset Signal & Timing"
              onClick={handleClear}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null
      }
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Signal Strength
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={slice.signalScore === "MEDIUM"}
                onChange={handleMediumScoreToggle}
              />
            }
            label={
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="body2">
                  Include companies with medium-score intent activity
                </Typography>
                <LightTooltip
                  placement="right"
                  title={
                    <Typography variant="body2" color="text.secondary">
                      Medium+ includes signal scores of 65–84 (and above).
                    </Typography>
                  }
                >
                  <IconButton
                    size="small"
                    aria-label="Signal strength help"
                    disableRipple
                    sx={{ p: 0.25 }}
                  >
                    <InfoOutlinedIcon
                      fontSize="inherit"
                      sx={{ fontSize: 16 }}
                    />
                  </IconButton>
                </LightTooltip>
              </Stack>
            }
            sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
          />
        </Box>

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Signal Recency
          </Typography>
          <FormControl size="small" fullWidth>
            <Select
              value={slice.numberOfCadences ?? ""}
              onChange={handleDateRangeChange}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) {
                  return INTENT_ALL_DATES_LABEL;
                }
                return getIntentCadenceLabel(selected as number);
              }}
            >
              <MenuItem value="">{INTENT_ALL_DATES_LABEL}</MenuItem>
              {INTENT_CADENCE_OPTIONS.map((option) => (
                <MenuItem key={option.label} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Stack>
    </AccordionSection>
  );
});

SignalTimingSection.displayName = "SignalTimingSection";
